import React, { useState, useEffect, useRef } from 'react';
import { 
  AlertTriangle, Plus, Search, Edit, Trash2, Eye, Loader2,
  Phone, Mail, Calendar, DollarSign, FileText, Upload, Download,
  Scale, Building, User, ChevronDown, ChevronUp, X, Check,
  CreditCard, Gavel, File, Paperclip, BarChart3, FileDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { morosidadesAPI, contactosAPI, expedientesAPI } from '../api';
import { toast } from '../hooks/use-toast';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2 
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES');
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const ESTADOS = [
  { value: 'activo', label: 'Activo', color: 'bg-red-100 text-red-800' },
  { value: 'en_negociacion', label: 'En Negociación', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'judicial', label: 'En Vía Judicial', color: 'bg-purple-100 text-purple-800' },
  { value: 'cerrado', label: 'Cerrado', color: 'bg-green-100 text-green-800' },
];

const TIPOS_ACTUACION = [
  { value: 'llamada', label: 'Llamada telefónica' },
  { value: 'email', label: 'Email' },
  { value: 'carta', label: 'Carta' },
  { value: 'burofax', label: 'Burofax' },
  { value: 'visita', label: 'Visita' },
  { value: 'reunion', label: 'Reunión' },
  { value: 'otro', label: 'Otro' },
];

const RESULTADOS_ACTUACION = [
  { value: 'sin_respuesta', label: 'Sin respuesta' },
  { value: 'compromiso_pago', label: 'Compromiso de pago' },
  { value: 'negativa', label: 'Negativa al pago' },
  { value: 'pago_parcial', label: 'Pago parcial acordado' },
  { value: 'aplazamiento', label: 'Aplazamiento solicitado' },
  { value: 'otro', label: 'Otro' },
];

const METODOS_PAGO = [
  { value: 'transferencia', label: 'Transferencia bancaria' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'bizum', label: 'Bizum' },
  { value: 'otro', label: 'Otro' },
];

const Morosidad = () => {
  const navigate = useNavigate();
  const [morosidades, setMorosidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [stats, setStats] = useState({});
  
  // Clientes y expedientes
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  
  // Dialogs
  const [isMainDialogOpen, setIsMainDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [editingMorosidad, setEditingMorosidad] = useState(null);
  const [viewingMorosidad, setViewingMorosidad] = useState(null);
  
  // Detail data
  const [pagos, setPagos] = useState([]);
  const [actuaciones, setActuaciones] = useState([]);
  const [adjuntos, setAdjuntos] = useState([]);
  
  // Form
  const [form, setForm] = useState({
    numero_expediente: '',
    expediente_id: '',
    cliente_id: '',
    cliente_nombre: '',
    cliente_email: '',
    cliente_telefono: '',
    fecha_inicio_reclamacion: new Date().toISOString().split('T')[0],
    total_deuda: 0,
    factura_emitida: '',
    notas: '',
    estado: 'activo',
    // Judicial
    en_demanda_judicial: false,
    expediente_demanda: '',
    expediente_demanda_id: '',
    juzgado: '',
    numero_procedimiento: '',
    fecha_demanda: ''
  });
  
  // Sub-forms
  const [newPago, setNewPago] = useState({ fecha_pago: '', cantidad: '', metodo_pago: '', referencia: '', notas: '' });
  const [newActuacion, setNewActuacion] = useState({ fecha: '', tipo: '', titulo: '', descripcion: '', resultado: '' });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [filterEstado]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterEstado) params.estado = filterEstado;
      
      const [morosidadesRes, statsRes, clientesRes, expedientesRes] = await Promise.all([
        morosidadesAPI.getAll(params),
        morosidadesAPI.getStats(),
        contactosAPI.getAll(),
        expedientesAPI.getAll()
      ]);
      
      setMorosidades(morosidadesRes.data);
      setStats(statsRes.data);
      setClientes(clientesRes.data.filter(c => c.tipo === 'cliente'));
      setExpedientes(expedientesRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los datos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadDetailData = async (morosidadId) => {
    try {
      const [pagosRes, actuacionesRes, adjuntosRes] = await Promise.all([
        morosidadesAPI.getPagos(morosidadId),
        morosidadesAPI.getActuaciones(morosidadId),
        morosidadesAPI.getAdjuntos(morosidadId)
      ]);
      setPagos(pagosRes.data);
      setActuaciones(actuacionesRes.data);
      setAdjuntos(adjuntosRes.data);
    } catch (error) {
      console.error('Error cargando detalles:', error);
    }
  };

  const resetForm = () => {
    setForm({
      numero_expediente: '',
      expediente_id: '',
      cliente_id: '',
      cliente_nombre: '',
      cliente_email: '',
      cliente_telefono: '',
      fecha_inicio_reclamacion: new Date().toISOString().split('T')[0],
      total_deuda: 0,
      factura_emitida: '',
      notas: '',
      estado: 'activo',
      en_demanda_judicial: false,
      expediente_demanda: '',
      expediente_demanda_id: '',
      juzgado: '',
      numero_procedimiento: '',
      fecha_demanda: ''
    });
    setEditingMorosidad(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsMainDialogOpen(true);
  };

  const handleEdit = (morosidad) => {
    setForm({
      numero_expediente: morosidad.numero_expediente || '',
      expediente_id: morosidad.expediente_id?.toString() || '',
      cliente_id: morosidad.cliente_id?.toString() || '',
      cliente_nombre: morosidad.cliente_nombre || '',
      cliente_email: morosidad.cliente_email || '',
      cliente_telefono: morosidad.cliente_telefono || '',
      fecha_inicio_reclamacion: morosidad.fecha_inicio_reclamacion || '',
      total_deuda: morosidad.total_deuda || 0,
      factura_emitida: morosidad.factura_emitida || '',
      notas: morosidad.notas || '',
      estado: morosidad.estado || 'activo',
      en_demanda_judicial: morosidad.en_demanda_judicial || false,
      expediente_demanda: morosidad.expediente_demanda || '',
      expediente_demanda_id: morosidad.expediente_demanda_id?.toString() || '',
      juzgado: morosidad.juzgado || '',
      numero_procedimiento: morosidad.numero_procedimiento || '',
      fecha_demanda: morosidad.fecha_demanda || ''
    });
    setEditingMorosidad(morosidad);
    setIsMainDialogOpen(true);
  };

  const handleView = async (morosidad) => {
    setViewingMorosidad(morosidad);
    await loadDetailData(morosidad.id);
    setIsDetailDialogOpen(true);
  };

  const handleDelete = async (morosidad) => {
    if (!window.confirm(`¿Eliminar la morosidad de "${morosidad.cliente_nombre}"?`)) return;
    
    try {
      await morosidadesAPI.delete(morosidad.id);
      toast({ title: 'Morosidad eliminada' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clientes.find(c => c.id.toString() === clienteId);
    if (cliente) {
      setForm({
        ...form,
        cliente_id: clienteId,
        cliente_nombre: cliente.nombre,
        cliente_email: cliente.email || '',
        cliente_telefono: cliente.telefono || ''
      });
    }
  };

  const handleExpedienteChange = (expId) => {
    const exp = expedientes.find(e => e.id.toString() === expId);
    if (exp) {
      setForm({
        ...form,
        expediente_id: expId,
        numero_expediente: exp.numero_expediente
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente_nombre) {
      toast({ title: 'Error', description: 'El nombre del cliente es obligatorio', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        expediente_id: form.expediente_id ? parseInt(form.expediente_id) : null,
        cliente_id: form.cliente_id ? parseInt(form.cliente_id) : null,
        total_deuda: parseFloat(form.total_deuda) || 0,
        expediente_demanda_id: form.expediente_demanda_id ? parseInt(form.expediente_demanda_id) : null
      };
      
      if (editingMorosidad) {
        await morosidadesAPI.update(editingMorosidad.id, data);
        toast({ title: 'Morosidad actualizada' });
      } else {
        await morosidadesAPI.create(data);
        toast({ title: 'Morosidad creada' });
      }
      
      setIsMainDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Pagos
  const handleAddPago = async () => {
    if (!newPago.cantidad || !newPago.fecha_pago) {
      toast({ title: 'Error', description: 'Fecha y cantidad son obligatorios', variant: 'destructive' });
      return;
    }
    
    try {
      await morosidadesAPI.createPago(viewingMorosidad.id, {
        ...newPago,
        cantidad: parseFloat(newPago.cantidad)
      });
      toast({ title: 'Pago registrado' });
      setNewPago({ fecha_pago: '', cantidad: '', metodo_pago: '', referencia: '', notas: '' });
      
      // Recargar
      const [morosidadRes, pagosRes] = await Promise.all([
        morosidadesAPI.getById(viewingMorosidad.id),
        morosidadesAPI.getPagos(viewingMorosidad.id)
      ]);
      setViewingMorosidad(morosidadRes.data);
      setPagos(pagosRes.data);
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo registrar el pago', variant: 'destructive' });
    }
  };

  const handleDeletePago = async (pagoId) => {
    if (!window.confirm('¿Eliminar este pago?')) return;
    try {
      await morosidadesAPI.deletePago(viewingMorosidad.id, pagoId);
      toast({ title: 'Pago eliminado' });
      const [morosidadRes, pagosRes] = await Promise.all([
        morosidadesAPI.getById(viewingMorosidad.id),
        morosidadesAPI.getPagos(viewingMorosidad.id)
      ]);
      setViewingMorosidad(morosidadRes.data);
      setPagos(pagosRes.data);
      loadData();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  // Actuaciones
  const handleAddActuacion = async () => {
    if (!newActuacion.titulo || !newActuacion.fecha) {
      toast({ title: 'Error', description: 'Fecha y título son obligatorios', variant: 'destructive' });
      return;
    }
    
    try {
      await morosidadesAPI.createActuacion(viewingMorosidad.id, newActuacion);
      toast({ title: 'Actuación registrada' });
      setNewActuacion({ fecha: '', tipo: '', titulo: '', descripcion: '', resultado: '' });
      
      const res = await morosidadesAPI.getActuaciones(viewingMorosidad.id);
      setActuaciones(res.data);
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleDeleteActuacion = async (actuacionId) => {
    if (!window.confirm('¿Eliminar esta actuación?')) return;
    try {
      await morosidadesAPI.deleteActuacion(viewingMorosidad.id, actuacionId);
      const res = await morosidadesAPI.getActuaciones(viewingMorosidad.id);
      setActuaciones(res.data);
      toast({ title: 'Actuación eliminada' });
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  // Adjuntos
  const handleUploadAdjunto = async (files) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo_adjunto', 'documento');
      
      try {
        await morosidadesAPI.uploadAdjunto(viewingMorosidad.id, formData);
        toast({ title: `Archivo "${file.name}" subido` });
      } catch (error) {
        toast({ title: 'Error', description: `No se pudo subir ${file.name}`, variant: 'destructive' });
      }
    }
    
    const res = await morosidadesAPI.getAdjuntos(viewingMorosidad.id);
    setAdjuntos(res.data);
  };

  // Descargar informe Word
  const handleDownloadInforme = async (morosidad) => {
    try {
      toast({ title: 'Generando informe...', description: 'Por favor espere' });
      const response = await morosidadesAPI.getInformeWord(morosidad.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Informe_Morosidad_${morosidad.numero_expediente || morosidad.id}.docx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Informe descargado' });
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo generar el informe', variant: 'destructive' });
    }
  };

  const handleDownloadAdjunto = async (adjunto) => {
    try {
      const response = await morosidadesAPI.downloadAdjunto(viewingMorosidad.id, adjunto.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', adjunto.nombre_original || adjunto.nombre);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo descargar', variant: 'destructive' });
    }
  };

  const handleDeleteAdjunto = async (adjuntoId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await morosidadesAPI.deleteAdjunto(viewingMorosidad.id, adjuntoId);
      const res = await morosidadesAPI.getAdjuntos(viewingMorosidad.id);
      setAdjuntos(res.data);
      toast({ title: 'Archivo eliminado' });
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const getEstadoBadge = (estado) => {
    const est = ESTADOS.find(e => e.value === estado);
    return est ? <Badge className={est.color}>{est.label}</Badge> : <Badge>{estado}</Badge>;
  };

  if (loading && morosidades.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-6 h-6 text-red-600" />
        <h1 className="text-2xl font-bold text-gray-900">Control de Morosidad</h1>
      </div>
      <p className="text-sm text-gray-600">
        Gestión de clientes con impagos y reclamaciones de deuda.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="border-t-4 border-t-red-500">
          <CardContent className="p-4">
            <p className="text-xs text-gray-600">Total Casos</p>
            <p className="text-2xl font-bold">{stats.total || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-yellow-500">
          <CardContent className="p-4">
            <p className="text-xs text-gray-600">Activos</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.activas || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-purple-500">
          <CardContent className="p-4">
            <p className="text-xs text-gray-600">En Judicial</p>
            <p className="text-2xl font-bold text-purple-600">{stats.judiciales || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-green-500">
          <CardContent className="p-4">
            <p className="text-xs text-gray-600">Cerrados</p>
            <p className="text-2xl font-bold text-green-600">{stats.cerradas || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-600">Deuda Total</p>
            <p className="text-lg font-bold">{formatCurrency(stats.total_deuda)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-600">Cobrado</p>
            <p className="text-lg font-bold text-green-600">{formatCurrency(stats.total_pagos)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-red-600">
          <CardContent className="p-4">
            <p className="text-xs text-gray-600">Pendiente</p>
            <p className="text-lg font-bold text-red-600">{formatCurrency(stats.deuda_pendiente)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros y acciones */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por cliente, expediente..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadData()}
                />
              </div>
            </div>
            <Select value={filterEstado || 'all'} onValueChange={(v) => setFilterEstado(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {ESTADOS.map(e => (
                  <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              className="bg-purple-600 text-white hover:bg-purple-700"
              onClick={() => navigate('/contabilidad/morosidad/estadisticas')}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              ESTADÍSTICAS
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleOpenNew}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVO
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Expediente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Contacto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Fecha Inicio</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Total Deuda</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Pagado</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Pendiente</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">Estado</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {morosidades.length > 0 ? (
                  morosidades.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <span className="text-blue-600 font-medium">{m.numero_expediente || '-'}</span>
                        {m.factura_emitida && (
                          <p className="text-xs text-gray-500">Fact: {m.factura_emitida}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium">{m.cliente_nombre}</p>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {m.cliente_email && (
                          <p className="flex items-center gap-1 text-gray-600">
                            <Mail className="w-3 h-3" /> {m.cliente_email}
                          </p>
                        )}
                        {m.cliente_telefono && (
                          <p className="flex items-center gap-1 text-gray-600">
                            <Phone className="w-3 h-3" /> {m.cliente_telefono}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(m.fecha_inicio_reclamacion)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(m.total_deuda)}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600">{formatCurrency(m.total_pagos)}</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-red-600">{formatCurrency(m.deuda_pendiente)}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          {getEstadoBadge(m.estado)}
                          {m.en_demanda_judicial && (
                            <Badge className="bg-purple-100 text-purple-800 text-xs">
                              <Gavel className="w-3 h-3 mr-1" /> Judicial
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleView(m)} title="Ver detalle">
                            <Eye className="w-4 h-4 text-blue-600" />
                          </button>
                          <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleDownloadInforme(m)} title="Descargar informe Word">
                            <FileDown className="w-4 h-4 text-green-600" />
                          </button>
                          <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleEdit(m)} title="Editar">
                            <Edit className="w-4 h-4 text-gray-600" />
                          </button>
                          <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleDelete(m)} title="Eliminar">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-4 py-12 text-center text-gray-500">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No hay registros de morosidad</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Crear/Editar Morosidad */}
      <Dialog open={isMainDialogOpen} onOpenChange={setIsMainDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMorosidad ? 'Editar Morosidad' : 'Nueva Morosidad'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Expediente */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Expediente vinculado</Label>
                  <Select value={form.expediente_id} onValueChange={handleExpedienteChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar expediente" />
                    </SelectTrigger>
                    <SelectContent>
                      {expedientes.map(exp => (
                        <SelectItem key={exp.id} value={exp.id.toString()}>
                          {exp.numero_expediente} - {exp.titulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nº Expediente (manual)</Label>
                  <Input 
                    value={form.numero_expediente}
                    onChange={(e) => setForm({...form, numero_expediente: e.target.value})}
                    placeholder="Ej: 2024/001"
                  />
                </div>
              </div>

              {/* Cliente */}
              <div>
                <Label>Cliente *</Label>
                <Select value={form.cliente_id} onValueChange={handleClienteChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cliente existente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Nombre y Apellidos *</Label>
                  <Input 
                    value={form.cliente_nombre}
                    onChange={(e) => setForm({...form, cliente_nombre: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={form.cliente_email}
                    onChange={(e) => setForm({...form, cliente_email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input 
                    value={form.cliente_telefono}
                    onChange={(e) => setForm({...form, cliente_telefono: e.target.value})}
                  />
                </div>
              </div>

              {/* Deuda */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Fecha Inicio Reclamación *</Label>
                  <Input 
                    type="date"
                    value={form.fecha_inicio_reclamacion}
                    onChange={(e) => setForm({...form, fecha_inicio_reclamacion: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Total Deuda (€)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={form.total_deuda}
                    onChange={(e) => setForm({...form, total_deuda: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Factura Emitida</Label>
                  <Input 
                    value={form.factura_emitida}
                    onChange={(e) => setForm({...form, factura_emitida: e.target.value})}
                    placeholder="Nº factura"
                  />
                </div>
              </div>

              <div>
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({...form, estado: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS.map(e => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notas</Label>
                <Textarea 
                  value={form.notas}
                  onChange={(e) => setForm({...form, notas: e.target.value})}
                  rows={3}
                />
              </div>

              {/* Sección Judicial */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <Gavel className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold">Demanda Judicial</h3>
                </div>
                
                <div className="flex items-center gap-2 mb-4">
                  <input 
                    type="checkbox"
                    id="en_demanda"
                    checked={form.en_demanda_judicial}
                    onChange={(e) => setForm({...form, en_demanda_judicial: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="en_demanda" className="cursor-pointer">
                    Se ha presentado demanda judicial
                  </Label>
                </div>

                {form.en_demanda_judicial && (
                  <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Expediente de Demanda</Label>
                        <Input 
                          value={form.expediente_demanda}
                          onChange={(e) => setForm({...form, expediente_demanda: e.target.value})}
                          placeholder="Nº expediente demanda"
                        />
                      </div>
                      <div>
                        <Label>Fecha Demanda</Label>
                        <Input 
                          type="date"
                          value={form.fecha_demanda}
                          onChange={(e) => setForm({...form, fecha_demanda: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Juzgado</Label>
                        <Input 
                          value={form.juzgado}
                          onChange={(e) => setForm({...form, juzgado: e.target.value})}
                          placeholder="Ej: Juzgado Primera Instancia nº 3"
                        />
                      </div>
                      <div>
                        <Label>Nº Procedimiento Judicial</Label>
                        <Input 
                          value={form.numero_procedimiento}
                          onChange={(e) => setForm({...form, numero_procedimiento: e.target.value})}
                          placeholder="Ej: 123/2024"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsMainDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle Morosidad */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Detalle de Morosidad - {viewingMorosidad?.cliente_nombre}
            </DialogTitle>
          </DialogHeader>
          
          {viewingMorosidad && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="info">Información</TabsTrigger>
                <TabsTrigger value="pagos">Pagos ({pagos.length})</TabsTrigger>
                <TabsTrigger value="actuaciones">Actuaciones ({actuaciones.length})</TabsTrigger>
                <TabsTrigger value="adjuntos">Adjuntos ({adjuntos.length})</TabsTrigger>
              </TabsList>

              {/* Tab Info */}
              <TabsContent value="info" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Datos del Cliente</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p><strong>Nombre:</strong> {viewingMorosidad.cliente_nombre}</p>
                      <p><strong>Email:</strong> {viewingMorosidad.cliente_email || '-'}</p>
                      <p><strong>Teléfono:</strong> {viewingMorosidad.cliente_telefono || '-'}</p>
                      <p><strong>Expediente:</strong> {viewingMorosidad.numero_expediente || '-'}</p>
                      <p><strong>Factura:</strong> {viewingMorosidad.factura_emitida || '-'}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Estado de la Deuda</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p><strong>Inicio reclamación:</strong> {formatDate(viewingMorosidad.fecha_inicio_reclamacion)}</p>
                      <p><strong>Total deuda:</strong> <span className="text-lg font-bold">{formatCurrency(viewingMorosidad.total_deuda)}</span></p>
                      <p><strong>Pagado:</strong> <span className="text-green-600 font-bold">{formatCurrency(viewingMorosidad.total_pagos)}</span></p>
                      <p><strong>Pendiente:</strong> <span className="text-red-600 text-lg font-bold">{formatCurrency(viewingMorosidad.deuda_pendiente)}</span></p>
                      <div className="pt-2">{getEstadoBadge(viewingMorosidad.estado)}</div>
                    </CardContent>
                  </Card>
                </div>

                {viewingMorosidad.en_demanda_judicial && (
                  <Card className="border-purple-300 bg-purple-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Gavel className="w-4 h-4 text-purple-600" />
                        Demanda Judicial
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p><strong>Expediente demanda:</strong> {viewingMorosidad.expediente_demanda || '-'}</p>
                      <p><strong>Juzgado:</strong> {viewingMorosidad.juzgado || '-'}</p>
                      <p><strong>Nº Procedimiento:</strong> {viewingMorosidad.numero_procedimiento || '-'}</p>
                      <p><strong>Fecha demanda:</strong> {formatDate(viewingMorosidad.fecha_demanda)}</p>
                    </CardContent>
                  </Card>
                )}

                {viewingMorosidad.notas && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Notas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap">{viewingMorosidad.notas}</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Tab Pagos */}
              <TabsContent value="pagos" className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Registrar Pago</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-2">
                      <Input 
                        type="date" 
                        value={newPago.fecha_pago}
                        onChange={(e) => setNewPago({...newPago, fecha_pago: e.target.value})}
                        placeholder="Fecha"
                      />
                      <Input 
                        type="number" 
                        step="0.01"
                        value={newPago.cantidad}
                        onChange={(e) => setNewPago({...newPago, cantidad: e.target.value})}
                        placeholder="Cantidad €"
                      />
                      <Select value={newPago.metodo_pago} onValueChange={(v) => setNewPago({...newPago, metodo_pago: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Método" />
                        </SelectTrigger>
                        <SelectContent>
                          {METODOS_PAGO.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        value={newPago.referencia}
                        onChange={(e) => setNewPago({...newPago, referencia: e.target.value})}
                        placeholder="Referencia"
                      />
                      <Button onClick={handleAddPago} className="bg-green-600 hover:bg-green-700">
                        <Plus className="w-4 h-4 mr-1" /> Añadir
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs">Fecha</th>
                          <th className="px-4 py-2 text-right text-xs">Cantidad</th>
                          <th className="px-4 py-2 text-left text-xs">Método</th>
                          <th className="px-4 py-2 text-left text-xs">Referencia</th>
                          <th className="px-4 py-2 text-center text-xs">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {pagos.length > 0 ? pagos.map((p) => (
                          <tr key={p.id}>
                            <td className="px-4 py-2 text-sm">{formatDate(p.fecha_pago)}</td>
                            <td className="px-4 py-2 text-sm text-right font-medium text-green-600">{formatCurrency(p.cantidad)}</td>
                            <td className="px-4 py-2 text-sm">{METODOS_PAGO.find(m => m.value === p.metodo_pago)?.label || p.metodo_pago || '-'}</td>
                            <td className="px-4 py-2 text-sm">{p.referencia || '-'}</td>
                            <td className="px-4 py-2 text-center">
                              <button onClick={() => handleDeletePago(p.id)} className="p-1 hover:bg-red-100 rounded">
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              No hay pagos registrados
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tab Actuaciones */}
              <TabsContent value="actuaciones" className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Nueva Actuación</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-4 gap-2">
                      <Input 
                        type="date" 
                        value={newActuacion.fecha}
                        onChange={(e) => setNewActuacion({...newActuacion, fecha: e.target.value})}
                      />
                      <Select value={newActuacion.tipo} onValueChange={(v) => setNewActuacion({...newActuacion, tipo: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_ACTUACION.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        value={newActuacion.titulo}
                        onChange={(e) => setNewActuacion({...newActuacion, titulo: e.target.value})}
                        placeholder="Título *"
                        className="col-span-2"
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <Textarea 
                        value={newActuacion.descripcion}
                        onChange={(e) => setNewActuacion({...newActuacion, descripcion: e.target.value})}
                        placeholder="Descripción..."
                        rows={2}
                        className="col-span-2"
                      />
                      <Select value={newActuacion.resultado} onValueChange={(v) => setNewActuacion({...newActuacion, resultado: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Resultado" />
                        </SelectTrigger>
                        <SelectContent>
                          {RESULTADOS_ACTUACION.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={handleAddActuacion} className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-1" /> Añadir
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-2">
                  {actuaciones.length > 0 ? actuaciones.map((a) => (
                    <Card key={a.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline">{formatDate(a.fecha)}</Badge>
                              {a.tipo && <Badge>{TIPOS_ACTUACION.find(t => t.value === a.tipo)?.label || a.tipo}</Badge>}
                              {a.resultado && (
                                <Badge className="bg-gray-100 text-gray-700">
                                  {RESULTADOS_ACTUACION.find(r => r.value === a.resultado)?.label || a.resultado}
                                </Badge>
                              )}
                            </div>
                            <h4 className="font-medium">{a.titulo}</h4>
                            {a.descripcion && <p className="text-sm text-gray-600 mt-1">{a.descripcion}</p>}
                          </div>
                          <button onClick={() => handleDeleteActuacion(a.id)} className="p-1 hover:bg-red-100 rounded">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  )) : (
                    <Card>
                      <CardContent className="py-8 text-center text-gray-500">
                        No hay actuaciones registradas
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* Tab Adjuntos */}
              <TabsContent value="adjuntos" className="space-y-4">
                <Card>
                  <CardContent className="p-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      multiple
                      onChange={(e) => handleUploadAdjunto(Array.from(e.target.files || []))}
                    />
                    <div 
                      className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-gray-50"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">Haz clic para seleccionar archivos</p>
                      <p className="text-xs text-gray-400">o arrastra y suelta aquí</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-0">
                    {adjuntos.length > 0 ? (
                      <div className="divide-y">
                        {adjuntos.map((adj) => (
                          <div key={adj.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <File className="w-8 h-8 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium">{adj.nombre_original || adj.nombre}</p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(adj.tamano)} • {formatDate(adj.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleDownloadAdjunto(adj)}>
                                <Download className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteAdjunto(adj.id)}>
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-gray-500">
                        <Paperclip className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p>No hay archivos adjuntos</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Cerrar
            </Button>
            <Button onClick={() => { setIsDetailDialogOpen(false); handleEdit(viewingMorosidad); }}>
              <Edit className="w-4 h-4 mr-2" /> Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Morosidad;
